import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { patchPhoneNumber, type PhoneNumber } from "../lib/phoneNumbers";
import { queryKeys } from "../lib/queryKeys";
import { requestErrorDescription } from "../lib/errors";
import { Button, Inline, InlineError, Stack, Text, TextInput } from "../ui";

export function PhoneNumberCapacity({ number }: { number: PhoneNumber }) {
  const [value, setValue] = useState(String(number.max_concurrent_calls));
  const client = useQueryClient();
  const update = useMutation({
    mutationFn: () => patchPhoneNumber(number.id, Number(value)),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: queryKeys.phoneNumbers.all });
    },
  });
  const valid =
    Number.isInteger(Number(value)) &&
    Number(value) > 0 &&
    Number(value) <= 2147483647;
  return (
    <Stack gap={2}>
      <Inline>
        <TextInput
          label={`Concurrent calls for ${number.address}`}
          type="number"
          min={1}
          step={1}
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
          }}
        />
        <Button
          disabled={
            !valid ||
            update.isPending ||
            Number(value) === number.max_concurrent_calls
          }
          onClick={() => {
            update.mutate();
          }}
          variant="secondary"
        >
          Save cap
        </Button>
      </Inline>
      <Text variant="muted">
        {number.provider === "sip"
          ? "Set this cap to your SIP trunk concurrent-channel capacity. The default is one channel until capacity is known. Private trunks have no public reputation engine or attestation to protect."
          : "Default: 3 simultaneous calls per number. Hiya/TNS analytics and STIR/SHAKEN attestation can flag heavy simultaneous calling as spam. This protects your number’s reputation, which is slow to repair. Add another number before raising the cap."}
      </Text>
      {update.isError && (
        <InlineError>{requestErrorDescription(update.error)}</InlineError>
      )}
    </Stack>
  );
}
